/**
 * Dependency injection (DI) seams for MFA.
 * Allows consuming applications (ss-ui-client, shipstation-lite) to inject
 * their own implementations for notifications and tracking.
 *
 * Transport is not a seam: MFA calls go through `httpClient`, which the
 * consuming app configures.
 *
 * Pattern mirrors client-common/src/services/logger.ts
 */

import type { MfaTone } from './mfa-types';
import type { ReactNode } from 'react';

/**
 * MFA notifier interface for showing/updating notifications.
 * Never import @mantine/notifications in client-common.
 */
export type MfaNotifier = {
  show: (opts: { message: ReactNode; tone: MfaTone }) => string;
  update: (id: string, opts: { message: ReactNode; tone: MfaTone }) => void;
};

/**
 * Default no-op notifier.
 */
const defaultNotifier: MfaNotifier = {
  show: () => Math.random().toString(36).slice(2),
  update: () => {
    // no-op
  },
};

/**
 * MFA tracker for analytics events.
 */
export type MfaTracker = (event: string, props: { source: string }) => void;

/**
 * Default no-op tracker.
 */
const defaultTracker: MfaTracker = () => {
  // no-op
};

// ---- Notifier ----

let mfaNotifier: MfaNotifier = defaultNotifier;

/**
 * Configure the MFA notifier.
 */
export const configureMfaNotifier = (notifier: MfaNotifier): void => {
  mfaNotifier = notifier;
};

/**
 * Get the configured MFA notifier, or default to no-op.
 */
export const getMfaNotifier = (): MfaNotifier => {
  return mfaNotifier;
};

// ---- Tracker ----

let mfaTrackerInstance: MfaTracker = defaultTracker;

/**
 * Configure the MFA tracker for analytics.
 */
export const configureMfaTracker = (tracker: MfaTracker): void => {
  mfaTrackerInstance = tracker;
};

/**
 * Get the configured MFA tracker, or default to no-op.
 */
export const getMfaTracker = (): MfaTracker => {
  return mfaTrackerInstance;
};
